import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, Output } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ApplicationCategory, ApplicationType } from '@intellio/shared/models';

export interface RelateAppsListItem {
  id: string;
  disabled?: boolean;
  disabledReason?: string;
  [key: string]: any;
}

@Component({
  selector: 'itc-relate-apps-list',
  templateUrl: './relate-apps-list.component.html',
  styleUrls: ['./relate-apps-list.component.scss'],
  imports: [
    CommonModule,
    MatButtonModule,
    MatCardModule,
    MatIconModule,
    MatTooltipModule,
  ],
})
export class RelateAppsListComponent {
  @Input() apps: RelateAppsListItem[] = [];
  @Input() selectedAppIds: string[] = [];
  @Input() categories: ApplicationCategory[] = [];
  @Input() applicationTypes: ApplicationType[] = [];
  @Input() getCategoryName?: (app: any) => string;
  @Input() getProjectId?: (udfDetails: any) => string;
  @Input() actionLabel = 'ADD';
  @Input() selectedActionLabel = 'ADDED';
  @Input() removeActionLabel = 'REMOVE';
  @Input() showRemoveAction = false;

  @Output() appSelected = new EventEmitter<RelateAppsListItem>();
  @Output() appRemoved = new EventEmitter<RelateAppsListItem>();

  isAppDisabled(app: RelateAppsListItem): boolean {
    return !!app.disabled;
  }

  isAppSelected(app: RelateAppsListItem): boolean {
    return this.selectedAppIds.includes(app.id);
  }

  selectApp(app: RelateAppsListItem) {
    if (this.isAppDisabled(app)) {
      return;
    }

    this.appSelected.emit(app);
  }

  removeApp(app: RelateAppsListItem) {
    this.appRemoved.emit(app);
  }

  getDisabledReason(app: RelateAppsListItem): string {
    return app.disabledReason || '';
  }

  getEnergySourceId(app: RelateAppsListItem): string {
    return app.energySourceIds?.[0] ?? app.energySourceId ?? 'NA';
  }

  getEnergySourceName(app: RelateAppsListItem): string {
    return app.system?.energySourceName ?? app.energySourceName ?? '';
  }

  getOwnerName(app: RelateAppsListItem): string {
    return (
      app.owner?.fullName ??
      app.ownerName ??
      app.customerFullName ??
      app.projectName ??
      ''
    );
  }

  getAddress(app: RelateAppsListItem): string {
    if (app.system?.fullAddress) {
      return app.system.fullAddress;
    }

    if (app.system?.addressFull) {
      return app.system.addressFull;
    }

    if (app.system?.addressLineOne) {
      return [
        `${app.system.addressLineOne} ${app.system.addressLineTwo || ''}`.trim(),
        [
          app.system.addressCity,
          app.system.addressState,
          app.system.addressZIP,
        ]
          .filter(Boolean)
          .join(' '),
      ]
        .filter(Boolean)
        .join(', ');
    }

    return [
      app.systemAddress,
      app.systemStreetAddress,
      app.systemAddressCityStateZIP,
    ]
      .filter(Boolean)
      .join(', ');
  }

  getAddedAddressLineOne(app: RelateAppsListItem): string {
    if (app.system?.addressLineOne) {
      return `${app.system.addressLineOne} ${
        app.system.addressLineTwo || ''
      }`.trim();
    }

    return app.systemStreetAddress ?? app.systemAddress ?? this.getAddress(app);
  }

  getAddedAddressLineTwo(app: RelateAppsListItem): string {
    if (app.system?.addressCity || app.system?.addressState) {
      return [
        app.system.addressCity,
        app.system.addressState,
        app.system.addressZIP,
      ]
        .filter(Boolean)
        .join(' ');
    }

    return app.systemAddressCityStateZIP ?? '';
  }

  getDisplayCategoryName(app: RelateAppsListItem): string {
    if (this.getCategoryName) {
      return this.getCategoryName(app);
    }

    if (app.categoryId != null) {
      return (
        this.categories?.find((category) => category.id === app.categoryId)
          ?.name ??
        app.categoryName ??
        app.categoryId
      );
    }

    const categoryId = this.applicationTypes?.find(
      (type) => type.id === app.typeId
    )?.categoryId;

    return (
      this.categories?.find((category) => category.id === categoryId)?.name ??
      app.categoryName ??
      categoryId ??
      ''
    );
  }

  getDisplayProjectId(app: RelateAppsListItem): string {
    const udfDetails = app.udfs ?? app.applicationUDFDetails ?? [];

    if (this.getProjectId) {
      return this.getProjectId(udfDetails);
    }

    let idValue = '';

    for (const udf of udfDetails) {
      if (
        udf.id === 'ProjectID' ||
        udf.id === 'JobID' ||
        udf.id === 'QueueNumber'
      ) {
        idValue = udf.value;
      }

      if (
        udf.udfId === 'ProjectID' ||
        udf.udfId === 'JobID' ||
        udf.udfId === 'QueueNumber' ||
        udf.id === 'QueueNumber'
      ) {
        idValue = udf.stringValue ?? udf.value ?? idValue;
      }
    }

    return idValue;
  }
}
